import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * The tab strip rendered in the page-header subheader row. Replaces the
 * old per-page RadioButton strips that lived in the actions slot — the
 * page still owns selection via `activeTab` / `onTabChange`.
 *
 * Re-clicking the already-active tab is a no-op (matches native radio
 * semantics): it never fires `onTabChange`, so navigate-driven tabs
 * (Game Workspace) don't push a redundant history entry.
 *
 * Keyboard model mirrors the radio group it replaced: roving tabindex
 * (only the active tab is in the tab order) with Arrow/Home/End moving
 * AND activating the focused tab (automatic activation), so arrow keys
 * change the selection exactly as they did on the old radios.
 */
export const PageHeaderTabs = ({ tabs = [], activeTab, onTabChange }) => {
    const listRef = useRef(null);

    const activeIndex = tabs.findIndex(t => t.key === activeTab);
    // When nothing matches (e.g. an external/URL-driven tab is "off"), keep
    // the first tab reachable by keyboard so the strip never traps focus.
    const rovingIndex = activeIndex < 0 ? 0 : activeIndex;

    const focusTab = (index) => {
        const btns = listRef.current?.querySelectorAll('[role="tab"]');
        btns?.[index]?.focus();
    };

    const handleKeyDown = (e) => {
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(e.key) || tabs.length === 0) return;
        e.preventDefault();
        let next;
        if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        else if (e.key === 'ArrowRight') next = (rovingIndex + 1) % tabs.length;
        else next = (rovingIndex - 1 + tabs.length) % tabs.length;
        const target = tabs[next];
        if (target && target.key !== activeTab) onTabChange?.(target.key);
        focusTab(next);
    };

    return (
        <div
            className="page-header-tabs"
            role="tablist"
            aria-orientation="horizontal"
            ref={listRef}
            onKeyDown={handleKeyDown}
        >
            {tabs.map((t, i) => (
                <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.key}
                    tabIndex={i === rovingIndex ? 0 : -1}
                    className={`page-header-tab ${activeTab === t.key ? 'active' : ''}`}
                    onClick={() => { if (t.key !== activeTab) onTabChange?.(t.key); }}
                >
                    {t.icon && (
                        <FontAwesomeIcon icon={t.icon} className="page-header-tab-icon" />
                    )}
                    {t.label}
                </button>
            ))}
        </div>
    );
};

export default PageHeaderTabs;
